import React, { useMemo, useState } from "react";
import {
  alpha,
  Box,
  Button,
  Chip,
  IconButton,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import FilterListOffRoundedIcon from "@mui/icons-material/FilterListOffRounded";
import BookmarkBorderIcon from "@mui/icons-material/BookmarkBorder";
import { useTranslation } from "react-i18next";
import {
  normalizeSearchFilterState,
  QUICK_STATUS_FILTER_LABELS,
  TAG_NO_TAG,
} from "../../utils/searchFilterState";
import { getDashboardSavedFilters } from "../../utils/dashboardSavedFilters";
import DashboardSavedFiltersMenu from "./DashboardSavedFiltersMenu";

const TIPO_RESP_LABELS = {
  fila: "Fila",
  humanos: "Humanos",
  chatbots: "Chatbots",
  agentes: "Agentes de IA",
};

/**
 * Remove um critério a partir do descritor `clear` do chip de resumo.
 */
export function applyDashboardFilterChipClear(state, clear) {
  if (!clear) return normalizeSearchFilterState(state);
  const next = { ...normalizeSearchFilterState(state) };

  if (Array.isArray(clear.fields)) {
    clear.fields.forEach((field) => {
      if (Array.isArray(next[field])) next[field] = [];
      else if (field === "tipo_responsavel") next.tipo_responsavel = "";
      else if (field === "ops_preset") next.ops_preset = "";
      else if (field === "date_field") next.date_field = "entrada";
      else if (field === "all_period") next.all_period = false;
      else if (typeof next[field] === "boolean") next[field] = false;
      else if (typeof next[field] === "string") next[field] = "";
    });
    return next;
  }

  const field = clear.field;
  if (!field) return next;

  if (Array.isArray(next[field])) {
    if (clear.value === undefined) next[field] = [];
    else next[field] = next[field].filter((v) => String(v) !== String(clear.value));
    return next;
  }

  if (Object.prototype.hasOwnProperty.call(clear, "value")) {
    next[field] = clear.value;
  } else if (typeof next[field] === "boolean") {
    next[field] = false;
  } else if (typeof next[field] === "string") {
    next[field] = "";
  }
  return next;
}

/**
 * Chips de resumo — um por critério, status individuais (sem truncar tudo numa string).
 * @returns {{ key: string, label: string, title?: string, tone?: 'default'|'emphasis', clear?: object }[]}
 */
export function buildDashboardAdvancedFilterSummaries(state, t, { omitAllPeriod = false } = {}) {
  const s = normalizeSearchFilterState(state);
  const items = [];
  const tr = (key, fallback, vars) =>
    t(`dashboardPage.filters.${key}`, { defaultValue: fallback, ...vars });

  const statuses = (s.filters || []).filter((f) => QUICK_STATUS_FILTER_LABELS.includes(f));
  statuses.forEach((status) => {
    items.push({
      key: `status:${status}`,
      label: status,
      title: tr("chipStatusTooltip", "Status: {{value}}", { value: status }),
      tone: "emphasis",
      clear: { field: "filters", value: status },
    });
  });

  if (s.tipo_responsavel && TIPO_RESP_LABELS[s.tipo_responsavel]) {
    const value = TIPO_RESP_LABELS[s.tipo_responsavel];
    items.push({
      key: "tipo_responsavel",
      label: value,
      title: tr("chipOwnerType", "Tipo de responsável: {{value}}", { value }),
      clear: { field: "tipo_responsavel", value: "" },
    });
  }

  const assigneeCount = s.ids_usuarios.length + s.ids_agentes.length;
  if (assigneeCount > 0) {
    items.push({
      key: "assignees",
      label: tr("chipAssigneesShort", "{{count}} responsável(is)", { count: assigneeCount }),
      title: tr("chipAssignees", "Responsáveis selecionados: {{count}}", { count: assigneeCount }),
      clear: { fields: ["ids_usuarios", "ids_agentes"] },
    });
  }

  const interactionCount = s.ids_usuarios_interacao.length + s.ids_agentes_interacao.length;
  if (interactionCount > 0) {
    items.push({
      key: "interaction",
      label: tr("chipInteractionShort", "{{count}} interação(ões)", { count: interactionCount }),
      title: tr("chipInteraction", "Operadores que interagiram: {{count}}", {
        count: interactionCount,
      }),
      clear: { fields: ["ids_usuarios_interacao", "ids_agentes_interacao"] },
    });
  }

  const typeCount =
    s.ids_tipos.length + s.ids_subtipo1.length + s.ids_subtipo2.length + s.ids_subtipo3.length;
  if (typeCount > 0) {
    items.push({
      key: "types",
      label: tr("chipTypesShort", "{{count}} tipo(s)", { count: typeCount }),
      title: tr("chipTypes", "Tipos / subtipos: {{count}}", { count: typeCount }),
      clear: { fields: ["ids_tipos", "ids_subtipo1", "ids_subtipo2", "ids_subtipo3"] },
    });
  }

  if (s.ids_origens.length) {
    items.push({
      key: "origins",
      label: tr("chipOriginsShort", "{{count}} canal(is)", { count: s.ids_origens.length }),
      title: tr("chipOrigins", "Canais / origens: {{count}}", { count: s.ids_origens.length }),
      clear: { fields: ["ids_origens"] },
    });
  }

  if (s.ids_produtos.length) {
    items.push({
      key: "products",
      label: tr("chipProductsShort", "{{count}} produto(s)", { count: s.ids_produtos.length }),
      title: tr("chipProducts", "Produtos: {{count}}", { count: s.ids_produtos.length }),
      clear: { fields: ["ids_produtos"] },
    });
  }

  const campaignCount = s.ids_campaigns.length + s.ids_whatsapp_campaigns.length;
  if (campaignCount > 0) {
    items.push({
      key: "campaigns",
      label: tr("chipCampaignsShort", "{{count}} campanha(s)", { count: campaignCount }),
      title: tr("chipCampaigns", "Campanhas: {{count}}", { count: campaignCount }),
      clear: { fields: ["ids_campaigns", "ids_whatsapp_campaigns"] },
    });
  }

  if (s.ids_motivos.length) {
    items.push({
      key: "motives",
      label: tr("chipMotivesShort", "{{count}} motivo(s)", { count: s.ids_motivos.length }),
      title: tr("chipMotives", "Motivos de perda: {{count}}", { count: s.ids_motivos.length }),
      clear: { fields: ["ids_motivos"] },
    });
  }

  if (s.ids_potenciais.length) {
    const potLabels = { 1: "Baixo", 2: "Médio", 3: "Alto" };
    s.ids_potenciais.forEach((id) => {
      const label = potLabels[id] || `#${id}`;
      items.push({
        key: `potencial:${id}`,
        label,
        title: tr("chipPotentialTooltip", "Potencial: {{value}}", { value: label }),
        tone: "emphasis",
        clear: { field: "ids_potenciais", value: id },
      });
    });
  }

  if (s.ops_preset) {
    const opsLabels = {
      overdue: tr("opsOverdue", "Atrasados"),
      due_today: tr("opsDueToday", "Para hoje"),
      stale: tr("opsStale", "Sem movimentação ({{days}}d)", { days: s.stale_days || 7 }),
    };
    items.push({
      key: "ops_preset",
      label: opsLabels[s.ops_preset] || s.ops_preset,
      title: opsLabels[s.ops_preset] || s.ops_preset,
      tone: "emphasis",
      clear: { fields: ["ops_preset"] },
    });
  }

  if (s.tags.length) {
    s.tags.forEach((tag) => {
      const label = tag === TAG_NO_TAG ? tr("noTag", "Sem tag") : tag;
      items.push({
        key: `tag:${tag}`,
        label,
        title: tr("chipTagTooltip", "Tag: {{value}}", { value: label }),
        clear: { field: "tags", value: tag },
      });
    });
  }

  if (s.date_field === "atualizacao") {
    items.push({
      key: "date_field",
      label: tr("chipDateFieldUpdateShort", "Por atualização"),
      title: tr("chipDateFieldUpdate", "Filtrar pela data de última atualização"),
      clear: { field: "date_field", value: "entrada" },
    });
  }

  if (s.all_period && !omitAllPeriod) {
    items.push({
      key: "all_period",
      label: tr("chipAllPeriod", "Todo período"),
      title: tr("chipAllPeriodTooltip", "Inclui todas as solicitações em aberto, sem limite de data"),
      tone: "emphasis",
      clear: { field: "all_period", value: false },
    });
  }

  return items;
}

/**
 * Controles de filtros do dashboard — visual premium, chips legíveis, copy clara.
 */
export default function DashboardAdvancedFiltersControl({
  filterState,
  criteriaCount = 0,
  onOpen,
  onClear,
  onRemoveChip,
  omitAllPeriodChip = false,
  section,
  onApplySaved,
}) {
  const { t } = useTranslation("settings");
  const theme = useTheme();
  const summaries = useMemo(
    () =>
      buildDashboardAdvancedFilterSummaries(filterState, t, {
        omitAllPeriod: omitAllPeriodChip,
      }),
    [filterState, t, omitAllPeriodChip]
  );

  const handleRemoveChip = (item) => {
    if (typeof onRemoveChip !== "function" || !item?.clear) return;
    onRemoveChip(applyDashboardFilterChipClear(filterState, item.clear));
  };
  const hasActive = criteriaCount > 0;
  const [savedMenuAnchor, setSavedMenuAnchor] = useState(null);
  const [savedEntries, setSavedEntries] = useState([]);
  const showSavedMenu = Boolean(section && onApplySaved);
  const refreshSavedEntries = () => {
    setSavedEntries(getDashboardSavedFilters(section));
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        width: "100%",
        minWidth: 0,
        px: hasActive ? 1 : 0,
        py: hasActive ? 0.5 : 0,
        borderRadius: 2,
        bgcolor: hasActive ? alpha(theme.palette.primary.main, 0.04) : "transparent",
        transition: "background-color 160ms ease",
      }}
    >
      <Button
        variant={hasActive ? "contained" : "outlined"}
        size="small"
        color="primary"
        startIcon={<TuneRoundedIcon sx={{ fontSize: 18 }} />}
        onClick={onOpen}
        aria-label={t("dashboardPage.filters.openAria", {
          defaultValue: "Abrir filtros das solicitações",
        })}
        endIcon={
          hasActive ? (
            <Box
              component="span"
              sx={{
                ml: 0.25,
                minWidth: 20,
                height: 20,
                px: 0.75,
                borderRadius: 999,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "0.7rem",
                fontWeight: 700,
                lineHeight: 1,
                bgcolor: "rgba(255,255,255,0.22)",
                color: "inherit",
              }}
            >
              {criteriaCount}
            </Box>
          ) : null
        }
        sx={{
          height: 34,
          whiteSpace: "nowrap",
          textTransform: "none",
          fontWeight: 600,
          fontSize: "0.8125rem",
          px: 1.5,
          flexShrink: 0,
          borderRadius: 2,
          boxShadow: "none",
          "&:hover": { boxShadow: "none" },
          "& .MuiButton-endIcon": { ml: 0.75 },
        }}
      >
        {hasActive
          ? t("dashboardPage.filters.adjust", { defaultValue: "Ajustar filtros" })
          : t("dashboardPage.filters.open", { defaultValue: "Filtrar solicitações" })}
      </Button>

      {hasActive ? (
        <Tooltip
          title={t("dashboardPage.filters.clearTooltip", {
            defaultValue: "Remover todos os filtros",
          })}
        >
          <IconButton
            size="small"
            onClick={onClear}
            aria-label={t("dashboardPage.filters.clear", { defaultValue: "Limpar" })}
            sx={{
              flexShrink: 0,
              color: "text.secondary",
              "&:hover": { color: "error.main", bgcolor: alpha(theme.palette.error.main, 0.08) },
            }}
          >
            <FilterListOffRoundedIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Tooltip>
      ) : null}

      {hasActive && summaries.length > 0 ? (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.75,
            flex: 1,
            minWidth: 0,
            overflowX: "auto",
            overflowY: "hidden",
            flexWrap: "nowrap",
            py: 0.25,
            scrollbarWidth: "thin",
            "&::-webkit-scrollbar": { height: 3 },
            "&::-webkit-scrollbar-thumb": {
              bgcolor: "divider",
              borderRadius: 2,
            },
          }}
        >
          {summaries.map((item) => (
            <Tooltip key={item.key} title={item.title || item.label} enterDelay={400}>
              <Chip
                size="small"
                label={item.label}
                onClick={onOpen}
                onDelete={
                  typeof onRemoveChip === "function" && item.clear
                    ? (e) => {
                        e?.stopPropagation?.();
                        handleRemoveChip(item);
                      }
                    : undefined
                }
                sx={{
                  height: 26,
                  fontSize: "0.75rem",
                  fontWeight: item.tone === "emphasis" ? 600 : 500,
                  flexShrink: 0,
                  borderRadius: 1.5,
                  borderColor:
                    item.tone === "emphasis"
                      ? alpha(theme.palette.primary.main, 0.35)
                      : "divider",
                  bgcolor:
                    item.tone === "emphasis"
                      ? alpha(theme.palette.primary.main, 0.08)
                      : alpha(theme.palette.text.primary, 0.04),
                  color: item.tone === "emphasis" ? "primary.main" : "text.primary",
                  "& .MuiChip-label": { px: 1 },
                  "& .MuiChip-deleteIcon": {
                    fontSize: 16,
                    color: "inherit",
                    opacity: 0.7,
                    "&:hover": { opacity: 1 },
                  },
                  "&:hover": {
                    bgcolor:
                      item.tone === "emphasis"
                        ? alpha(theme.palette.primary.main, 0.14)
                        : alpha(theme.palette.text.primary, 0.08),
                  },
                }}
              />
            </Tooltip>
          ))}
        </Box>
      ) : !hasActive ? (
        <Typography
          variant="body2"
          color="text.secondary"
          noWrap
          sx={{
            display: { xs: "none", md: "block" },
            flex: 1,
            minWidth: 0,
            fontSize: "0.8125rem",
            pl: 0.5,
          }}
        >
          {t("dashboardPage.filters.hint", {
            defaultValue: "Refine por status, canal, tipo ou responsável",
          })}
        </Typography>
      ) : (
        <Box sx={{ flex: 1 }} />
      )}

      {showSavedMenu ? (
        <>
          <Tooltip
            title={t("dashboardPage.savedFilters.title", { defaultValue: "Filtros salvos" })}
          >
            <IconButton
              size="small"
              onClick={(e) => {
                refreshSavedEntries();
                setSavedMenuAnchor(e.currentTarget);
              }}
              aria-label={t("dashboardPage.savedFilters.title", {
                defaultValue: "Filtros salvos",
              })}
              sx={{ flexShrink: 0, color: "text.secondary" }}
            >
              <BookmarkBorderIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>
          <DashboardSavedFiltersMenu
            anchorEl={savedMenuAnchor}
            open={Boolean(savedMenuAnchor)}
            onClose={() => setSavedMenuAnchor(null)}
            section={section}
            entries={savedEntries}
            filterState={filterState}
            criteriaCount={criteriaCount}
            onApply={onApplySaved}
            onSaved={refreshSavedEntries}
          />
        </>
      ) : null}
    </Box>
  );
}
